import {
  AuthResponseSchema,
  InvitationPreviewSchema,
  type AuthResponse,
  type InvitationPreview,
} from '@escala/contracts';
import type { z } from 'zod';

import { preferenceStorage } from '@/privacy/consent';

import { ApiError, request, type RequestOptions } from '../http';

/**
 * Sessão com a API: o access token fica só em memória (nunca no `localStorage`) e o
 * refresh token num cookie httpOnly que o navegador manda sozinho para `/api/v1/auth`.
 *
 * O refresh é **em fila única**: a API troca o refresh token a cada uso e derruba a sessão
 * se um token já trocado voltar — duas renovações ao mesmo tempo (duas requisições com o
 * token vencido) seriam lidas como roubo.
 */

const AUTH = '/api/v1/auth';
/** A organização que o Administrador da plataforma escolheu (volta ao recarregar). */
const ACTIVE_ORG_KEY = 'escala:active-org';
/** O endereço da última igreja usada no login ("Último acesso"). */
const LAST_ORG_KEY = 'escala:org';

function readStored(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key: string, value: string | null) {
  try {
    if (value) window.localStorage.setItem(key, value);
    else window.localStorage.removeItem(key);
  } catch {
    // Armazenamento bloqueado: vale só nesta aba.
  }
}

/**
 * Troca a organização ativa (só o Administrador da plataforma; a API recusa os demais).
 * `null` volta à organização do próprio acesso.
 */
export function setActiveOrg(orgId: string | null): void {
  activeOrg = orgId;
  writeStored(ACTIVE_ORG_KEY, orgId);
}

export function currentActiveOrg(): string | null {
  return activeOrg;
}

/** Último endereço de igreja usado no login. */
export function lastOrgSlug(): string | null {
  return readStored(LAST_ORG_KEY);
}

export function rememberOrgSlug(slug: string): void {
  // Funcional ("última igreja do login"): só com o consentimento.
  if (slug) preferenceStorage.set(LAST_ORG_KEY, slug);
  else writeStored(LAST_ORG_KEY, null);
}

let accessToken: string | null = null;
/** Organização ativa escolhida pelo Administrador da plataforma (cabeçalho `X-Org-Id`). */
let activeOrg: string | null = readStored(ACTIVE_ORG_KEY);
let refreshing: Promise<AuthResponse | null> | null = null;
const listeners = new Set<(session: AuthResponse | null) => void>();

/** Avisa quando a sessão cai (refresh recusado) ou muda. */
export function onSessionChange(listener: (session: AuthResponse | null) => void): () => void {
  listeners.add(listener);

  return () => listeners.delete(listener);
}

function setSession(session: AuthResponse | null) {
  accessToken = session?.accessToken ?? null;
  listeners.forEach((l) => l(session));
}

export function currentAccessToken(): string | null {
  return accessToken;
}

const withCookie = { credentials: 'include' } as const;

/**
 * `remember` = "Manter conectado neste dispositivo"; sem ele, a sessão acaba com o navegador.
 * `org` = o endereço da igreja escolhida no passo "Encontre sua igreja".
 */
export async function login(
  email: string,
  password: string,
  remember = true,
  org?: string
): Promise<AuthResponse> {
  const session = await request(`${AUTH}/login`, {
    method: 'POST',
    body: { email, password, remember, ...(org ? { org } : {}) },
    schema: AuthResponseSchema,
    ...withCookie,
  });

  // A sessão nova abre na organização do acesso.
  setActiveOrg(null);
  if (org) rememberOrgSlug(org);
  setSession(session);

  return session;
}

/** Quem convidou, o e-mail e se o link ainda vale — sem consumir o convite. */
export function invitationPreview(token: string): Promise<InvitationPreview> {
  return request(`${AUTH}/invitations/${encodeURIComponent(token)}`, { schema: InvitationPreviewSchema });
}

/** Cria a senha do convite, com o aceite dos Termos e da Política (`termsVersion`). */
export async function acceptInvitation(
  token: string,
  password: string,
  termsVersion: string
): Promise<AuthResponse> {
  const session = await request(`${AUTH}/accept-invitation`, {
    method: 'POST',
    body: { token, password, termsVersion },
    schema: AuthResponseSchema,
    ...withCookie,
  });

  setSession(session);

  return session;
}

/** "Criar meu acesso" no fim do link de completar o cadastro: encerra o link e abre a sessão. */
export async function acceptProfileLink(
  token: string,
  email: string,
  password: string,
  termsVersion: string
): Promise<AuthResponse> {
  const session = await request(`${AUTH}/profile-account`, {
    method: 'POST',
    body: { token, email, password, termsVersion },
    schema: AuthResponseSchema,
    ...withCookie,
  });

  setSession(session);

  return session;
}

export function requestPasswordReset(email: string, org?: string): Promise<unknown> {
  return request(`${AUTH}/password-reset`, { method: 'POST', body: { email, ...(org ? { org } : {}) } });
}

export function confirmPasswordReset(token: string, password: string): Promise<unknown> {
  return request(`${AUTH}/password-reset/confirm`, { method: 'POST', body: { token, password } });
}

/** "Alterar senha" do perfil: confere a atual e encerra as sessões dos outros dispositivos. */
export function changePassword(currentPassword: string, newPassword: string): Promise<unknown> {
  return authorized(`${AUTH}/password`, {
    method: 'POST',
    body: { currentPassword, newPassword },
    ...withCookie,
  });
}

/** Sai de todos os dispositivos, menos deste navegador (o cookie diz qual sessão manter). */
export function endOtherSessions(): Promise<unknown> {
  return authorized(`${AUTH}/sessions/end-others`, { method: 'POST', ...withCookie });
}

/** Renova a sessão pelo cookie. `null` quando não há sessão (ou ela foi encerrada). */
export function refresh(): Promise<AuthResponse | null> {
  refreshing ??= request(`${AUTH}/refresh`, { method: 'POST', schema: AuthResponseSchema, ...withCookie })
    .then((session) => {
      setSession(session);

      return session;
    })
    .catch((error: unknown) => {
      if (error instanceof ApiError && error.status === 401) {
        setSession(null);

        return null;
      }

      throw error;
    })
    .finally(() => {
      refreshing = null;
    });

  return refreshing;
}

export async function logout(): Promise<void> {
  try {
    await request(`${AUTH}/logout`, { method: 'POST', ...withCookie });
  } finally {
    setActiveOrg(null);
    setSession(null);
  }
}

/**
 * Chamada autenticada: manda o access token e, se ele venceu (401), renova uma vez e
 * repete. Refresh recusado derruba a sessão (`onSessionChange(null)`) e o erro segue.
 */
export async function authorized<TSchema extends z.ZodType>(
  path: string,
  options: RequestOptions<TSchema> & { schema: TSchema }
): Promise<z.infer<TSchema>>;
export async function authorized(path: string, options?: RequestOptions): Promise<unknown>;
export async function authorized(path: string, options: RequestOptions = {}): Promise<unknown> {
  const attempt = () =>
    request(path, {
      ...options,
      headers: {
        ...options.headers,
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        ...(activeOrg ? { 'X-Org-Id': activeOrg } : {}),
      },
    });

  try {
    return await attempt();
  } catch (error) {
    if (!(error instanceof ApiError) || error.status !== 401) throw error;
    if (!(await refresh())) throw error;

    return attempt();
  }
}
